import React from "react";
import { CheckCircle2, XCircle, CircleDot } from "lucide-react";

/* ── Shared scoring primitives — used by Analysis (Phase 2) AND Results (Phase 3)
   so the two screens are pixel-identical (same colors, same rows, same rings). ── */

export const STATE_STYLE = {
  matched: "text-emerald-700 bg-emerald-100 border-emerald-200 dark:text-emerald-400 dark:bg-emerald-950/30 dark:border-emerald-900",
  partial: "text-amber-700 bg-amber-100 border-amber-200 dark:text-amber-400 dark:bg-amber-950/30 dark:border-amber-900",
  missing: "text-rose-700 bg-rose-100 border-rose-200 dark:text-rose-400 dark:bg-rose-950/30 dark:border-rose-900",
};

export const STATE_ICON = { matched: CheckCircle2, partial: CircleDot, missing: XCircle };

export const PRIORITY_CHIP = {
  high: "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400",
  medium: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
  low: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
};

export const KW_GROUPS = [
  { key: "technical", title: "Hard / Technical Skills" },
  { key: "soft", title: "Soft Skills" },
  { key: "domain", title: "Domain Terms" },
];

export function scoreColor(v) {
  return v >= 75 ? "#059669" : v >= 50 ? "#d97706" : "#e11d48";
}

export function ScoreRing({ value, size = 144, label = "Match", strokeWidth = 9 }) {
  const v = Math.max(0, Math.min(100, Math.round(value || 0)));
  const r = 52, c = 2 * Math.PI * r;
  const color = scoreColor(v);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="match-score-ring">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={r} fill="none" stroke="currentColor" strokeWidth={strokeWidth} className="text-border/60" />
        <circle cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (v / 100) * c} style={{ transition: "stroke-dashoffset 1s ease-out" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-black tracking-tighter text-foreground">{v}<span className="text-xl">%</span></span>
        <span className="text-[0.62rem] font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
      </div>
    </div>
  );
}

export function KeywordRow({ it, group, idx }) {
  const Icon = STATE_ICON[it.state] || CircleDot;
  return (
    <div className="flex items-center justify-between gap-3 py-3 px-5 border-b border-border/30 last:border-0 hover:bg-muted/20 transition-colors" data-testid={`keyword-row-${group}-${idx}`}>
      <div className="flex items-center gap-2.5 min-w-0">
        <Icon size={16} className={it.state === "matched" ? "text-emerald-600 dark:text-emerald-400" : it.state === "partial" ? "text-amber-600 dark:text-amber-400" : "text-rose-600 dark:text-rose-400"} />
        <span className="font-medium text-foreground truncate">{it.skill}</span>
        <span className={`text-[0.6rem] font-mono uppercase px-1.5 py-0.5 rounded ${it.importance === "required" ? "bg-foreground/10 text-foreground" : "bg-muted text-muted-foreground"}`}>{it.importance === "required" ? "Req" : "Pref"}</span>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="hidden xl:inline text-[0.7rem] font-mono text-muted-foreground">{it.jd_count}× JD · {it.resume_count}× résumé</span>
        <span className={`rounded-full px-2.5 py-0.5 text-sm font-semibold border ${STATE_STYLE[it.state]}`}>{it.earned}/{it.max}</span>
      </div>
    </div>
  );
}

export function KeywordBreakdownCard({ breakdown, title = "JD Keyword Breakdown", testid = "keyword-breakdown-list" }) {
  const kb = breakdown || {};
  return (
    <div className="bg-card border border-border/50 rounded-xl overflow-hidden shadow-sm" data-testid={testid}>
      <div className="px-5 py-4 border-b border-border/50 flex items-center justify-between">
        <h3 className="text-lg font-semibold tracking-tight text-foreground">{title}</h3>
        <span className="text-xs font-mono text-muted-foreground">{kb.keyword_count || 0} keywords</span>
      </div>
      {KW_GROUPS.map((grp) => {
        const items = kb.groups?.[grp.key] || [];
        if (items.length === 0) return null;
        return (
          <div key={grp.key}>
            <div className="px-5 py-2 bg-muted/30 text-[0.7rem] font-mono uppercase tracking-[0.15em] text-muted-foreground">{grp.title} · {items.length}</div>
            {items.map((it, idx) => <KeywordRow key={idx} it={it} group={grp.key} idx={idx} />)}
          </div>
        );
      })}
    </div>
  );
}
